import React, { useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Typography,
} from '@mui/material'
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'

import { t } from '@lingui/macro'
import AccountDataService from '../DataServices/AccountDataService'
import FormTextField from '../utils/controls/FormTextField'

type FormLoginFields = {
  email: string
  password: string
}

export default function Login(): JSX.Element {
  console.log('Load Login Component')

  const navigate: NavigateFunction = useNavigate()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [submitWarning, setSubmitWarning] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')

  const formSchema = yup.object().shape({
    email: yup
      .string()
      .label(t`The email address`)
      .required()
      .email(),
    password: yup
      .string()
      .label(t`The password`)
      .required(),
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormLoginFields>({
    resolver: yupResolver(formSchema),
  })

  const handleShowPasswordClick = (): void => {
    setShowPassword(!showPassword)
  }

  const handlePasswordMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
  }

  const handleFormSubmit = (data: FormLoginFields): void => {
    setSubmitWarning('')
    setSubmitError('')

    AccountDataService.login(data.email, data.password)
      .then(() => {
        navigate('/')
      })
      .catch((err) => {
        console.log('ERROR: An error occurred during sign in', err, err.response)
        if (err.response.status === 401 && err.response.data === 'no_active_account') {
          setSubmitWarning(t`No active account found with the given credentials.`)
        } else if (
          err.response.status === 401 &&
          err.response.data === 'email_verification_failed'
        ) {
          setSubmitWarning(
            t`Your account is not verified. We have sent an email to you for verification. Follow the link provided to finalize the signup process.`,
          )
        } else {
          setSubmitError(t`An error occurred during sign in, please try again.`)
        }
      })
  }

  return (
    <>
      <Typography component="h1" variant="h5">
        {t`Login`}
      </Typography>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(handleFormSubmit)}
        sx={{ mt: 1, width: '100%' }}
      >
        <FormTextField
          autoComplete='off'
          autoFocus
          errorText={errors.email?.message}
          label={t`Email Address`}
          margin="normal"
          registerReturn={register('email')}
        />
        <FormTextField
          autoComplete='off'
          errorText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => handleShowPasswordClick()}
                  onMouseDown={handlePasswordMouseDown}
                  edge="end"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          label={t`Password`}
          margin="normal"
          registerReturn={register('password')}
          type={showPassword ? 'text' : 'password'}
        />
        {submitWarning !== '' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {submitWarning}
          </Alert>
        )}
        {submitError !== '' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
        <Button color="primary" fullWidth sx={{ mb: 2, mt: 3 }} type="submit" variant="contained">
          {t`Sign In`}
        </Button>
        <Grid container>
          <Grid item xs>
            <Link href="/auth/forgot-password/" variant="body2">
              {t`Forgot password?`}
            </Link>
          </Grid>
          <Grid item>
            <Link href="/auth/signup/" variant="body2">
              {t`Don't have an account? Sign Up`}
            </Link>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
